import { useBackend } from "../app/BackendProvider";
import { describeError } from "../app/useResource";
import { shortVehicleNumber } from "../app/format";
import { useNetworkId } from "../config/ConfigProvider";
import { useActivePostParam } from "../posts/PostProvider";
import { DeviationValue, LineBadge, PanelHeading, PositionDot } from "./bits";
import { usePolledResource } from "./hooks";

/**
 * Synoptique condensé (F1.5, CDC §5.4) — prend la place du journal des mesures
 * abandonné. Supervision globale : chaque ligne, ses véhicules en circulation.
 * Identification non nominative : n° de bus, sortie, n° de service.
 */
export function CondensedSynoptic({
  onSelectJourney,
  collapsed = false,
  onToggleCollapsed,
}: {
  onSelectJourney: (journeyId: string) => void;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const postParam = useActivePostParam();

  const { data, error, loading } = usePolledResource(
    (signal) => backend.api.getSynoptic(networkId, postParam ? { post: postParam } : {}, signal),
    [backend, networkId, postParam, collapsed],
    collapsed ? 3_600_000 : 15_000, // replié : on cesse de rafraîchir
  );

  return (
    <div>
      <PanelHeading
        aside={
          onToggleCollapsed ? (
            <button
              type="button"
              className="button is-small is-white"
              aria-expanded={!collapsed}
              aria-label={collapsed ? "Déplier le synoptique condensé" : "Replier le synoptique condensé"}
              onClick={onToggleCollapsed}
              style={{ height: 30, width: 30, padding: 0, lineHeight: 1, fontSize: "1.05rem", fontWeight: 700 }}
            >
              {collapsed ? "▸" : "▾"}
            </button>
          ) : undefined
        }
      >
        Synoptique condensé
      </PanelHeading>
      {collapsed ? null : loading && !data ? (
        <p className="has-text-grey is-size-7">Chargement…</p>
      ) : error && !data ? (
        <p className="has-text-danger is-size-7">{describeError(error)}</p>
      ) : (
        <div className="mae-scroll-y" style={{ maxHeight: 220 }}>
          <table className="table is-fullwidth is-narrow" style={{ fontSize: "11px", background: "var(--sentinelle-surface)" }}>
            <thead>
              <tr>
                <th className="ovl" style={{ width: 44 }}>Ligne</th>
                <th className="ovl">Véhicules en circulation</th>
              </tr>
            </thead>
            <tbody>
              {(data?.lines ?? []).map((row) => (
                <tr key={row.lineId}>
                  <td>
                    <LineBadge lineId={row.lineId} size={22} />
                  </td>
                  <td>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {row.vehicles.map((v) => (
                        <button
                          key={v.journeyId}
                          type="button"
                          className="button is-small is-white"
                          style={{ height: 28, padding: "0 7px", gap: 6 }}
                          onClick={() => onSelectJourney(v.journeyId)}
                          title={`${v.deviationLabel}${v.runId ? ` · sortie ${v.runId}` : ""}${v.serviceId ? ` · service ${v.serviceId}` : ""}${v.atTerminus ? " · au terminus" : ""}`}
                        >
                          <PositionDot type={v.positionType} />
                          <span className="mono" style={{ fontWeight: 600 }}>
                            {v.runId ?? shortVehicleNumber(v.vehicleDisplayNumber)}
                          </span>
                          <DeviationValue seconds={v.deviationSeconds} outOfTolerance={Math.abs(v.deviationSeconds) >= 180} style={{ fontSize: "0.72rem" }} />
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
